/**
 * AP-37 Detailblatt O 7.3 — **Preisspiegel**: Zeilen sind Materialzeilen mit
 * mindestens einer Antwort, je antwortendem Lieferanten eine Spalte mit dem
 * Nettopreis je Einheit der Kostenzeile (EP ÷ PE × (1 − Rabatt)). Der günstigste
 * ist markiert; gewählt wird je Zeile eine Zelle, oben mit Voreinstellungen.
 * „Preise übernehmen" zeigt zuerst die Wirkung (7.4).
 *
 * Eine Vergleichsmatrix, keine Liste: Die Spalten sind die Lieferanten der
 * Antworten — deshalb ohne Spalten-Layout, mit Filter, Gruppierung und Summen (O-43).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { fetchCalculationComparison, fetchComparison } from '../../api/calculation-requests.api'
import type { ComparisonResult, ComparisonRow } from '../../types/calculation-requests.types'
import { UebernehmenDialog } from './UebernehmenDialog'

const QUELLE: Record<string, string> = { ARTICLE: 'Artikel', MANUAL: 'von Hand', OFFER: 'Angebot' }
const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })
const tag = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '')
/** Nettopreis mit bis zu vier Stellen (Kleinteile), sonst im Geldformat. */
const preis = (x: number) => (Math.abs(x * 100 - Math.round(x * 100)) > 1e-6 ? x.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : geldText(x))

export function PreisspiegelReiter({
  calculationId,
  nurDiese = false,
  neuLaden,
  onGeaendert,
}: {
  calculationId?: string
  /**
   * AP-39 Nachtrag 2 (6.2): nur diese eine Kalkulation — das Fenster „Angebote übernehmen" in der Kalkulation.
   * Ohne Auswahl der Kalkulation und ohne Gruppen; gelesen mit ihrem Leserecht statt dem des Einkaufs (PE-119).
   */
  nurDiese?: boolean
  neuLaden?: number
  onGeaendert?: () => void
}) {
  const [daten, setDaten] = useState<ComparisonResult | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [kalkulation, setKalkulation] = useState(calculationId ?? '')
  const [lieferant, setLieferant] = useState('')
  const [nurOffene, setNurOffene] = useState(true)
  const [gruppe, setGruppe] = useState<'CALCULATION' | 'MATERIAL'>('CALCULATION')
  const [wahl, setWahl] = useState<Map<string, string>>(new Map())
  const [uebernehmen, setUebernehmen] = useState(false)
  const [kalkOptionen, setKalkOptionen] = useState<Map<string, string>>(new Map())
  const eine = nurDiese && !!calculationId

  const laden = useCallback(() => {
    const anfrage = eine
      ? fetchCalculationComparison(calculationId!, { supplierId: lieferant || undefined, open: nurOffene })
      : fetchComparison({ calculationId: kalkulation || undefined, supplierId: lieferant || undefined, open: nurOffene })
    anfrage
      .then((d) => {
        setDaten(d)
        setFehler(null)
        setKalkOptionen((alt) => new Map([...alt, ...d.rows.map((r) => [r.calculationId, `${r.calculationLabel} ${r.calculationName}`] as const)]))
        // Vorgabe: der günstigste je Zeile — nur für Zeilen, die noch keine Wahl haben.
        setWahl((alt) => {
          const neu = new Map<string, string>()
          for (const r of d.rows) {
            const bisher = alt.get(r.costLineId)
            if (bisher && r.offers.some((o) => o.requestLineId === bisher)) neu.set(r.costLineId, bisher)
            else if (r.bestRequestLineId && r.bestRequestLineId !== r.appliedRequestLineId) neu.set(r.costLineId, r.bestRequestLineId)
          }
          return neu
        })
      })
      .catch((e) => setFehler(apiErrorText(e, 'Der Preisspiegel ließ sich nicht laden.')))
  }, [eine, calculationId, kalkulation, lieferant, nurOffene])
  useEffect(() => laden(), [laden, neuLaden])

  const lieferanten = daten?.suppliers ?? []
  const gruppen = useMemo(() => {
    const m = new Map<string, { label: string; rows: ComparisonRow[] }>()
    for (const r of daten?.rows ?? []) {
      const key = gruppe === 'CALCULATION' ? r.calculationId : r.materialKey
      const label = gruppe === 'CALCULATION' ? `${r.calculationLabel} ${r.calculationName}` : `${r.name} (${r.unitCode ?? '—'})`
      const g = m.get(key) ?? { label, rows: [] }
      g.rows.push(r)
      m.set(key, g)
    }
    return [...m.values()]
  }, [daten, gruppe])

  const voreinstellung = (art: 'BEST' | 'NONE' | string) => {
    const neu = new Map<string, string>()
    for (const r of daten?.rows ?? []) {
      if (art === 'NONE') continue
      if (art === 'BEST') {
        if (r.bestRequestLineId) neu.set(r.costLineId, r.bestRequestLineId)
        continue
      }
      const o = r.offers.find((x) => x.supplierId === art && x.netPrice !== null && x.unitMatches)
      if (o) neu.set(r.costLineId, o.requestLineId)
    }
    setWahl(neu)
  }

  const auswahl = [...wahl].filter(([costLineId, requestLineId]) => {
    const r = daten?.rows.find((x) => x.costLineId === costLineId)
    return r && r.appliedRequestLineId !== requestLineId
  })
  const spalten = 4 + lieferanten.length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 text-sm">
        {!eine && (
          <label className="block">
            <span className="mb-0.5 block text-xs text-muted-foreground">Kalkulation</span>
            <select className="rounded-md border border-border bg-background px-2 py-1" value={kalkulation} onChange={(e) => setKalkulation(e.target.value)}>
              <option value="">alle</option>
              {[...kalkOptionen].map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="mb-0.5 block text-xs text-muted-foreground">Lieferant</span>
          <select className="rounded-md border border-border bg-background px-2 py-1" value={lieferant} onChange={(e) => setLieferant(e.target.value)}>
            <option value="">alle</option>
            {lieferanten.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        {!eine && (
          <label className="block">
            <span className="mb-0.5 block text-xs text-muted-foreground">Gruppieren nach</span>
            <select className="rounded-md border border-border bg-background px-2 py-1" value={gruppe} onChange={(e) => setGruppe(e.target.value as typeof gruppe)}>
              <option value="CALCULATION">Kalkulation</option>
              <option value="MATERIAL">Material</option>
            </select>
          </label>
        )}
        <label className="flex items-center gap-1.5 pb-1">
          <input type="checkbox" checked={nurOffene} onChange={(e) => setNurOffene(e.target.checked)} />
          nur noch nicht übernommene
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Wahl:</span>
          <button type="button" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => voreinstellung('BEST')}>
            günstigster je Zeile
          </button>
          <select className="rounded-md border border-border bg-background px-2 py-1 text-xs" value="" onChange={(e) => e.target.value && voreinstellung(e.target.value)}>
            <option value="">alles von …</option>
            {lieferanten.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <button type="button" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => voreinstellung('NONE')}>
            keine
          </button>
          <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!auswahl.length} onClick={() => setUebernehmen(true)}>
            {auswahl.length} Preis{auswahl.length === 1 ? '' : 'e'} übernehmen …
          </button>
        </div>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-xs">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">Position · Material</th>
              <th className="px-3 py-1.5 text-right font-medium">Menge</th>
              <th className="px-3 py-1.5 text-right font-medium">jetzt</th>
              {lieferanten.map((l) => (
                <th key={l.id} className="px-3 py-1.5 text-right font-medium">
                  {l.name}
                </th>
              ))}
              <th className="px-3 py-1.5 text-right font-medium">Wirkung</th>
            </tr>
          </thead>
          <tbody>
            {daten && daten.rows.length === 0 && (
              <tr>
                <td colSpan={spalten} className="px-4 py-8 text-center text-muted-foreground">
                  {nurOffene
                    ? 'Keine Antworten, die noch nicht übernommen sind.'
                    : eine
                      ? 'Noch keine Antworten mit Preis — die Antwort des Lieferanten steht an der Preisanfrage.'
                      : 'Noch keine Antworten — im Reiter Anfragen erfassen.'}
                </td>
              </tr>
            )}
            {gruppen.map((g) => [
              // Für eine einzige Kalkulation braucht es keine Gruppenzeile — ihr Name steht im Fenster.
              !eine && (
                <tr key={`g-${g.label}`} className="border-b border-border bg-muted/40">
                  <td colSpan={spalten} className="px-3 py-1 font-medium">
                    {g.label}
                  </td>
                </tr>
              ),
              ...g.rows.map((r) => {
                const gewaehlt = wahl.get(r.costLineId)
                const o = r.offers.find((x) => x.requestLineId === gewaehlt)
                return (
                  <tr key={r.costLineId} className="border-b border-border/60 align-top">
                    <td className="px-3 py-1.5">
                      <div>{r.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {gruppe === 'MATERIAL' && (
                          <Link to={`/calculations/${r.calculationId}`} className="text-primary hover:underline">
                            {r.calculationLabel}
                          </Link>
                        )}{' '}
                        {r.positionLabel}
                        {!r.writable && <span className="ml-1 text-amber-700 dark:text-amber-300">festgeschrieben</span>}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {mengeText(r.quantity)} {r.unitCode}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                      {r.current.netPrice != null ? preis(r.current.netPrice) : '—'}
                      <div className="text-xs text-muted-foreground">
                        {QUELLE[r.current.priceSource] ?? r.current.priceSource} {tag(r.current.priceDate)}
                      </div>
                    </td>
                    {lieferanten.map((l) => {
                      const x = r.offers.find((y) => y.supplierId === l.id)
                      if (!x) return <td key={l.id} className="px-3 py-1.5" />
                      const bestes = x.requestLineId === r.bestRequestLineId
                      const an = gewaehlt === x.requestLineId
                      const waehlbar = x.netPrice !== null && x.unitMatches
                      return (
                        <td key={l.id} className={`px-2 py-1 text-right ${x.applied ? 'text-muted-foreground' : ''}`}>
                          {x.notOffered ? (
                            <span className="text-xs text-muted-foreground">nicht angeboten</span>
                          ) : (
                            <label className={`inline-flex cursor-pointer flex-col items-end rounded-md px-2 py-0.5 ${an ? 'bg-primary/15 ring-1 ring-primary' : 'hover:bg-muted'} ${waehlbar ? '' : 'cursor-not-allowed opacity-60'}`}>
                              <span className="flex items-center gap-1 tabular-nums">
                                <input
                                  type="radio"
                                  name={`wahl-${r.costLineId}`}
                                  className="sr-only"
                                  disabled={!waehlbar || x.applied}
                                  checked={an}
                                  onChange={() => setWahl((alt) => new Map(alt).set(r.costLineId, x.requestLineId))}
                                />
                                {bestes && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-label="günstigster" />}
                                {x.netPrice != null ? preis(x.netPrice) : '—'}
                              </span>
                              {/* 7.4a (O-60): verglichen in der Einheit der Zeile, angeboten in einer anderen */}
                              {x.converted && x.offerNetPrice != null && (
                                <span className="text-xs tabular-nums text-muted-foreground" title="angeboten in einer anderen Einheit, umgerechnet über das Gewicht">
                                  {preis(x.offerNetPrice)} je {x.offerUnitCode}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {x.leadTimeDays != null ? `${x.leadTimeDays} Tage` : ''}
                                {x.applied ? ' · übernommen' : ''}
                              </span>
                              {x.alternative && (
                                <span className="inline-flex items-center gap-0.5 text-xs text-amber-700 dark:text-amber-300" title={x.alternative}>
                                  <AlertTriangle className="h-3 w-3" /> Alternative
                                </span>
                              )}
                              {!x.unitMatches && <span className="text-xs text-destructive">Einheit geändert</span>}
                            </label>
                          )}
                        </td>
                      )
                    })}
                    <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-xs">
                      {o && o.costNew != null ? (
                        <>
                          {r.current.cost != null ? geldText(r.current.cost) : '—'} → <span className="font-medium">{geldText(o.costNew)}</span>
                        </>
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                )
              }),
            ])}
          </tbody>
          {lieferanten.length > 0 && (daten?.rows.length ?? 0) > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/50 text-xs font-medium">
                <td className="px-3 py-1.5">{daten!.rows.length} Zeilen</td>
                <td />
                <td />
                {lieferanten.map((l) => (
                  <td key={l.id} className="px-3 py-1.5 text-right tabular-nums">
                    {geldText(l.total)}
                    <div className="font-normal text-muted-foreground">{l.missing ? `${l.missing} von ${daten!.rows.length} ohne Preis` : 'alle Zeilen'}</div>
                  </td>
                ))}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {uebernehmen && (
        <UebernehmenDialog
          choices={auswahl.map(([costLineId, requestLineId]) => ({ costLineId, requestLineId }))}
          onClose={() => setUebernehmen(false)}
          onFertig={() => {
            setUebernehmen(false)
            laden()
            onGeaendert?.()
          }}
        />
      )}
    </div>
  )
}
